/* eslint-disable @typescript-eslint/no-require-imports */
/* eslint-disable import/no-webpack-loader-syntax */
import { $t } from '@/i18n';
import { forIn, memoize } from 'lodash';
import openVersionChooser from './openVersionChooser';
import { message } from 'ant-design-vue';
import { getTestBaseUrl, getSearchDevOpen } from './useGogo';
const fetchEditorRollingReleaseConfig = async (useRollingRelease) => {
    try {
        const status = getSearchDevOpen() ? 'test' : 'prod';
        const runtimeVersion = require('@tencent/wuji-gogo-runtime/package.json')?.version || '';
        const rawFilter = [
            `status="${status}"`,
            `runtimeVersion="${runtimeVersion}"`,
        ].join('&');
        const filter = encodeURIComponent(rawFilter);
        const url = `${useRollingRelease}&filter=${filter}&size=5&sort=_ctime&order=desc`;
        const res = await fetch(url);
        const data = await res.json();
        return data.data;
    }
    catch (error) {
        console.error('fetchEditorRollingReleaseConfig error', error);
        return null;
    }
};
export const loadVue3Stuff = memoize(async () => {
    const { moduleManage } = window.wujiComp;
    const { allWebpackExternals } = moduleManage;
    if (!allWebpackExternals.vue3)
        await moduleManage.loadVue3Dependencies();
    // @ts-ignore
    const tdesign = await import('@tencent/wuji-vue-next-adaptor/src/utils/global').then(x => x.loadTDesign());
    const ans = {
        vue: allWebpackExternals.vue3,
        'ant-design-vue': allWebpackExternals.vue3Antd,
        'tdesign-vue-next': tdesign,
        Vue3Adaptor: allWebpackExternals.Vue3Adaptor,
    };
    forIn(ans, (value, key) => {
        window.define(`gogo-env/${key}`, [], () => value);
    });
    return ans;
});
const loadStylesheetWithTimeout = (href, timeoutMs = 3000) => new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = href;
    let finished = false;
    const done = (ok) => {
        if (finished)
            return;
        finished = true;
        clearTimeout(timer);
        if (ok) {
            resolve(link);
        }
        else {
            reject(new Error(`Failed to load stylesheet: ${href}`));
        }
    };
    const timer = setTimeout(() => done(false), timeoutMs);
    link.onload = () => done(true);
    link.onerror = () => done(false);
    document.head.appendChild(link);
});
const loadScriptWithTimeout = (src, timeoutMs = 15000) => new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    let finished = false;
    const done = (ok) => {
        if (finished)
            return;
        finished = true;
        clearTimeout(timer);
        if (ok) {
            resolve(script);
        }
        else {
            reject(new Error(`Failed to load script: ${src}`));
        }
    };
    const timer = setTimeout(() => done(false), timeoutMs);
    script.onload = () => done(true);
    script.onerror = () => done(false);
    document.head.appendChild(script);
});
const applyEditorAssets = async (url) => {
    // 清除历史资源
    removeEditorAssets();
    // 并行发起CSS和JS加载
    const cssPromise = loadStylesheetWithTimeout(`${url}/for-cdn.css`);
    const jsPromise = loadScriptWithTimeout(`${url}/for-cdn.js`);
    // 同时等待两者完成
    try {
        await Promise.all([cssPromise, jsPromise]);
        const { vue } = await loadVue3Stuff();
        const js = await new Promise((resolve, reject) => {
            let attempts = 0;
            const maxAttempts = 50;
            const timer = setInterval(() => {
                attempts += 1;
                const { getWujiGogoEditor } = window;
                if (typeof getWujiGogoEditor !== 'function') {
                    if (attempts >= maxAttempts) {
                        clearInterval(timer);
                        reject(new Error(`[xy-client] getWujiGogoEditor not available after ${maxAttempts} attempts`));
                    }
                    return;
                }
                clearInterval(timer);
                resolve(getWujiGogoEditor({ vue }));
            }, 100);
        });
        return js;
    }
    catch (error) {
        // 统一错误处理（任一失败都会进入catch）
        removeEditorAssets(); // 回滚已加载的资源
        throw error;
    }
};
const removeEditorAssets = () => {
    try {
        document.querySelectorAll('link[rel="stylesheet"]').forEach((el) => {
            const href = el.href || '';
            if (href.endsWith('/for-cdn.css'))
                el.remove();
        });
        document.querySelectorAll('script').forEach((el) => {
            const src = el.src || '';
            if (src.endsWith('/for-cdn.js'))
                el.remove();
        });
    }
    catch (_) {
        /* ignore */
    }
};
const loadEditorWithStrategy = async (baseUrl, useRollingRelease) => {
    const testBaseUrl = getTestBaseUrl();
    if (testBaseUrl) {
        try {
            return await applyEditorAssets(testBaseUrl);
        }
        catch (e) {
            message.error($t('k_4e3cd06d'));
            console.error('[xy-client] Test version failed to load:', e);
            return;
        }
    }
    if (useRollingRelease && !XY_USE_LOCAL_GOGO) {
        const data = await fetchEditorRollingReleaseConfig(useRollingRelease);
        const rollingCandidates = Array.isArray(data) ? data.filter((x) => x?.baseUrl) : [];
        const failedUrls = new Set();
        const picked = rollingCandidates[0] || null;
        if (picked) {
            const candidate = picked.baseUrl || '';
            try {
                return await applyEditorAssets(candidate);
            }
            catch (e) {
                failedUrls.add(candidate);
                // 内联重试与版本选择逻辑
                while (true) {
                    const selectedUrl = await openVersionChooser(rollingCandidates, baseUrl, failedUrls);
                    if (!selectedUrl) {
                        // 用户取消或未选择，回退到默认 baseUrl
                        return applyEditorAssets(baseUrl);
                    }
                    try {
                        return await applyEditorAssets(selectedUrl);
                    }
                    catch (loadError) {
                        failedUrls.add(selectedUrl);
                        console.warn(`[xy-client] Version ${selectedUrl} failed to load:`, loadError);
                    }
                }
            }
        }
        // 没有候选版本，直接使用默认版本
        return applyEditorAssets(baseUrl);
    }
    // 未启用测试模式与滚动发布，直接使用默认版本
    return applyEditorAssets(baseUrl);
};
const gogoLibMapSource = {
    editor: async () => {
        const gogoEditorVersion = require('@tencent/wuji-gogo-editor/package.json')?.version || '';
        const serveSubPath = XY_USE_LOCAL_GOGO ? 'gogo-editor-dev' : `gogo-editor-${gogoEditorVersion}`;
        const baseUrl = `${window.GLOBAL_INFO.staticFileCdn || window.GLOBAL_INFO.pathPrefix}/xystatic/${serveSubPath}`;
        const $setupShiki = import('./setupShiki').then(x => x.setupShiki);
        const useRollingRelease = window?.GLOBAL_INFO?.wujiGoConfig?.useRollingRelease || '';
        const js = await loadEditorWithStrategy(baseUrl, useRollingRelease);
        const setupShiki = await $setupShiki;
        setupShiki(js.monaco);
        return js;
    },
    runtime: async () => {
        await loadVue3Stuff(); // 确保 vue3 ready
        const [js] = await Promise.all([
            import('@tencent/wuji-gogo-runtime'),
            // @ts-ignore - style import may not have types
            import('@tencent/wuji-gogo-runtime/index.css'),
            window?.w?.ai?.init(),
        ]);
        return js;
    },
};
export const loadGogoLib = memoize(async (key) => {
    await loadVue3Stuff();
    const js = await gogoLibMapSource[key]();
    return js;
});
/**
 * 一个 Vue2 异步的组件，提供了 GogoEditor
 *
 * see https://v2.vuejs.org/v2/guide/components-dynamic-async#Handling-Loading-State
 */
export const AsyncGogoEditor = memoize(() => {
    const component = (async () => {
        const stuff = await loadVue3Stuff();
        const gogo = await loadGogoLib('editor');
        const { pinia } = gogo;
        const comp = stuff.Vue3Adaptor.default(gogo.GogoEditor, { library: 'tdesign', plugins: [pinia] });
        return comp;
    })();
    return {
        component,
        delay: 300,
        loading: {
            render() {
                return <div class='flex flex-col items-center justify-center'>
          <t-loading />
          <div class='animate-fade-in mt-4 text-gray-8'>{$t('k_a5866f2b')}</div>
        </div>;
            },
        },
    };
});
/**
 * 一个 Vue2 异步的组件，提供了 GogoRuntime
 *
 * see https://v2.vuejs.org/v2/guide/components-dynamic-async#Handling-Loading-State
 */
export const AsyncGogoRuntime = memoize(() => {
    const component = (async () => {
        const stuff = await loadVue3Stuff();
        const gogo = await loadGogoLib('runtime');
        const comp = stuff.Vue3Adaptor.default(gogo.default, { library: 'tdesign', plugins: [] });
        return comp;
    })();
    return {
        component,
        delay: 300,
        loading: {
            render() {
                return <div class='flex flex-col items-center justify-center'>
          <t-loading />
        </div>;
            },
        },
    };
});
